import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useEventManager } from "../hooks/useEventManager";
import { EventToast } from "../components/Toast";
import { EventDetails } from "../components/EventDetails";
import { EventTransport, type TransportValue } from "../components/EventTransport";
import { NO_TRANSPORT_CODE } from "../hooks/useEventTransport";
import { EventPermissions } from "../components/EventPermissions";
import { EventGroups } from "../components/EventGroups";
import { EventNotices } from "../components/EventNotices";
import { EventAttendees } from "../components/EventAttendees";
import EventBanners from "../components/EventBanners";
import EventAddress from "../components/EventAddress";
import { Loader } from "../components/Loader";
import AccessDenied from "../components/AccessDenied";
import EventChangelog from "../components/EventChangelog";
import {
	Container,
	Form,
	Tabs,
	Tab,
	Button,
	Modal,
	OverlayTrigger,
	Tooltip,
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	Badge,
} from "react-bootstrap";
import "react-dual-listbox/lib/react-dual-listbox.css";
import "./Event.css";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const FIRST_REMINDER_DEFAULT = 30;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SECOND_REMINDER_DEFAULT = 7;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const LOCK_DAYS_DEFAULT = 3;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Event = (_props: any) => {
	let { id } = useParams<"id">();
	const [isSaving, setSaving] = useState(false);
	const [showDevInfo, setShowDevInfo] = useState(false);
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { events, eventsLoaded, eventLoadError, eventSaving, canEdit, updateEvents } =
		useEventManager(id);
	const [showToast, setShowToast] = useState(false);
	const [showErrorModal, setShowErrorModal] = useState(false);
	//const toggleShowToast = () => setShowToast(!showToast);
	//const toggleErrorModal = () => setShowErrorModal(!showErrorModal);
	const [toastMessage, setToastMessage] = useState("");
	const [toastVariant, setToastVariant] = useState<"success" | "error">(
		"success"
	);
	const [active, setActive] = useState(events.ActiveAt ? true : false);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleSubmit = async (e: any) => {
		e.preventDefault();
		if (handleValidation()) {
			setSaving(true);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			let noticeIdArray = events.Notices.map((item: any) => {
				return item.id;
			});
			const saveData = {
				_id: events._id,
				AdditionalNotes: events.AdditionalNotes,
				Description: { Long: events.Description?.Long ?? "" },
				MasterEventId: events.MasterEventId,
				Location: {
					Lat: events.Location.Lat || null,
					Lon: events.Location.Lon || null,
					Address: events.Location.Address || null,
				},
				Approval: {
					Required: events.Approval?.Required || false,
					Compulsory: events.Approval?.Compulsory || false,
					FormUrl: events.Approval?.Urls?.Event || null,
					MoreInfoUrl: events.Approval?.Urls?.MoreInfo || null,
					MedicalCheck: events.Approval?.Checks?.Medical || null,
					EmergencyContact: events.Approval?.Checks?.EmergencyContact || null,
					DietaryCheck: events.Approval?.Checks?.Dietary === true,
					PermissionLockDays: events.Approval?.RSVP?.LockDays || 0,
					FirstReminderDays: events.Approval?.RSVP?.ReminderDays
						? events.Approval?.RSVP?.ReminderDays[0]
						: 0,
					SecondReminderDays: events.Approval?.RSVP?.ReminderDays
						? events.Approval?.RSVP?.ReminderDays[1]
						: 0,
					TermsAndConditions: events.Approval?.TermsAndConditions || null,
					ExternalPermissionForm:
						events.Approval?.ExternalPermissionForm || null,
					ExternalMedicalForm: events.Approval?.ExternalMedicalForm || null,
				},
				Banner: {
					_id: events.Banner?._id || null,
					ImageUrl: events.Banner?.ImageUrl || null,
				},
				CarerApprovals: events.AttendeeApprovals
					? events.AttendeeApprovals
					: [],
				Forms: events.Forms ? events.Forms : [],
				SelectedGroups: events.SelectedGroups || [],
				Notices: noticeIdArray,
				ActiveAt: events.ActiveAt || null,
				// Left out when never loaded or set; null clears it.
				Transport: events.Transport
					? {
							Code: events.Transport.Code,
							Notes: events.Transport.Notes ?? null,
							AcknowledgementRequired:
								events.Transport.AcknowledgementRequired === true,
							AcknowledgementText:
								(events.Transport.AcknowledgementText ?? "").trim() || null,
						}
					: events.Transport,
			};
			console.log("save data:", saveData);

			try {
				const result = await updateEvents(saveData);
				setToastVariant("success");
				setToastMessage(result?.Result ?? "Event updated");
			} catch (error) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const status = (error as any)?.response?.status;
				setToastVariant("error");
				if (status === 401 || status === 403) {
					setToastMessage(
						"You don't have permission to save changes to this event. Please contact IT to request Event Manager edit access."
					);
				} else {
					setToastMessage(
						"Could not save your changes. Please try again, and contact IT if the issue continues."
					);
				}
			} finally {
				setSaving(false);
				setShowToast(true);
			}
		}
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const timeoutId = setTimeout(() => {
			setShowToast(false);
		}, 5000);
	};

	const handleValidation = () => {
		if (events?.Approval?.Compulsory) {
			console.log("Compulsary checked: ", events.Approval.TermsAndConditions);
			if (
				!events.Approval.TermsAndConditions ||
				events.Approval.TermsAndConditions.trim() === ""
			) {
				console.log("TnC required");
				setToastMessage(
					"Please fill in the terms and conditions for this event."
				);
				setShowErrorModal(true);
				return false;
			}
		}
		if (
			events?.Transport?.AcknowledgementRequired === true &&
			!(events.Transport.AcknowledgementText ?? "").trim()
		) {
			setToastMessage(
				"Please enter the acknowledgement text for this event."
			);
			setShowErrorModal(true);
			return false;
		}
		return true;
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleAddress = (address: any) => {
		events["Location"] = address;
	};

	function updateOrAdd(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		arr: [{ id: any; value: string }],
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		id: any,
		newValue: string
	): [{ id: string; value: string }] {
		const index = arr.findIndex((obj) => obj.id === id);
		if (index !== -1) {
			arr[index].value = newValue;
		} else {
			arr.push({ id: id, value: newValue });
		}
		return arr;
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleAttendees = (attendees: any) => {
		let attendeeArray = [];
		if (events && typeof events === 'object' && "AttendeeApprovals" in events) {
			attendeeArray = events["AttendeeApprovals"];
		}
		for (const obj in attendees) {
			attendeeArray = updateOrAdd(
				attendeeArray,
				attendees[obj].id,
				attendees[obj].value // ? "going" : "not going"
			);
		}
		events["AttendeeApprovals"] = attendeeArray;
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleAttendeeForms = (forms: any) => {
		let submissionsArray = [];
		if (events && typeof events === 'object' && "Forms" in events) {
			submissionsArray = events["Forms"];
		}
		for (const obj in forms) {
			submissionsArray = updateOrAdd(
				submissionsArray,
				forms[obj].id,
				forms[obj].value
			);
		}
		if (submissionsArray.length > 0) events["Forms"] = submissionsArray;
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleGroups = (groups: any) => {
		events["SelectedGroups"] = groups;
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleNotices = (notices: any) => {
		events["Notices"] = notices;
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleBanner = (banner: any) => {
		events["Banner"] = banner;
	};
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleDetailsChange = (details: any) => {
		if (!events?.Approval) {
			events["Approval"] = {
				Urls: {},
			};
		}
		if (!events["Approval"]) {
			events["Approval"] = { Urls: {} };
		}
		if (typeof events["Approval"] !== "object" || events["Approval"] === null) {
			events["Approval"] = { Urls: {} };
		}
		events["AdditionalNotes"] = details?.AdditionalNotes;
		if (details?.Description?.Long !== undefined) {
			events["Description"] = {
				...events["Description"],
				Long: details.Description.Long,
			};
		}
	};
	// Transport is sent as { Code, Notes, AcknowledgementRequired,
	// AcknowledgementText }, or null to clear it (None, no notes, no acknowledgement).
	const handleTransportChange = (value: TransportValue) => {
		events["Transport"] =
			value.code === NO_TRANSPORT_CODE &&
			!value.notes.trim() &&
			!value.acknowledgementRequired
				? null
				: {
						Code: value.code,
						Notes: value.notes,
						AcknowledgementRequired: value.acknowledgementRequired,
						AcknowledgementText: value.acknowledgementText,
					};
	};
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handlePermissionsChange = (approval: any) => {
		events["Approval"] = approval;
	};

	function decodeHtmlEntities(encodedString: string): string {
		const elem = document.createElement("textarea");
		elem.innerHTML = encodedString;
		return elem.value;
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleActiveToggle = (e: any) => {
		const currentDateTime = new Date().toISOString();
		events.ActiveAt = e.target.checked ? currentDateTime : null;
		setActive(e.target.checked);
	};

	useEffect(() => {
		if (eventsLoaded) {
			setActive(events.ActiveAt ? true : false);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [events]);

	if (!eventsLoaded && eventLoadError) {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		if ((eventLoadError as any)?.response?.status === 403) {
			return <AccessDenied />;
		}
		return <Loader />;
	}

	if (eventsLoaded) {
		console.log("eventsLoaded", active);
		return (
			<>
				<EventToast
					title={"Event Manager"}
					message={toastMessage}
					delay={5000}
					show={showToast}
					variant={toastVariant}
				/>
				<Container className="mt-3">
					<nav aria-label="breadcrumb" className="breadcrumb">
						<ol className="breadcrumb">
							<li className="breadcrumb-item breadcrumb-item">
								<Link to="../">Events</Link>
							</li>
							<li
								className="breadcrumb-item active"
								aria-current="page"
								onClick={() => {
									setShowDevInfo(true);
								}}
							>
								{decodeHtmlEntities(events["Title"])}
							</li>
						</ol>
					</nav>
					<Form>
						<OverlayTrigger
							placement="bottom"
							overlay={
								!canEdit ? (
									<Tooltip id="save-tooltip">
										You don't have permission to edit this event.
									</Tooltip>
								) : (
									<span />
								)
							}
						>
							<span className="float-right d-inline-block">
								<Button
									type="button"
									variant="primary"
									id="save_event"
									size="sm"
									disabled={isSaving || !canEdit}
									style={!canEdit ? { pointerEvents: "none" } : {}}
									onClick={handleSubmit}
								>
									Save
								</Button>
							</span>
						</OverlayTrigger>
						<Form.Check // prettier-ignore
							type="switch"
							id="activate-permission"
							label="Activate Permission"
							className="float-right"
							onChange={(e) => {
								handleActiveToggle(e);
							}}
							checked={active}
							disabled={!canEdit}
						/>
						<Tabs
							defaultActiveKey="details"
							id="event-details"
							className="mb-3"
						>
							<Tab eventKey="details" title="Details">
								<fieldset disabled={!canEdit} className="border-0 p-0 m-0">
									<EventDetails
										initialData={events || {}}
										onValueChange={handleDetailsChange}
									/>
								</fieldset>
							</Tab>
							<Tab eventKey="permissions" title="Permissions">
								<fieldset disabled={!canEdit} className="border-0 p-0 m-0">
									<EventPermissions
										initialData={events?.Approval || {}}
										onValueChange={handlePermissionsChange}
									/>
								</fieldset>
							</Tab>
							<Tab eventKey="transport" title="Transport">
								<EventTransport
									initialData={events?.Transport}
									onValueChange={handleTransportChange}
								/>
							</Tab>
							<Tab eventKey="address" title="Address">
								<fieldset disabled={!canEdit} className="border-0 p-0 m-0">
									<EventAddress
										initialData={events?.Location || {}}
										onValueChange={handleAddress}
									/>
								</fieldset>
							</Tab>
							<Tab eventKey="banner" title="Banner">
								<fieldset disabled={!canEdit} className="border-0 p-0 m-0">
									<EventBanners
										initialData={events?.Banner || {}}
										onValueChange={handleBanner}
									/>
								</fieldset>
							</Tab>
							<Tab eventKey="groups" title="Groups">
								<fieldset disabled={!canEdit} className="border-0 p-0 m-0">
									<EventGroups
										initialData={events?.Groups || []}
										eventData={events}
										groupTypes={events?.GroupTypes || []}
										onValueChange={handleGroups}
									/>
								</fieldset>
							</Tab>
							<Tab eventKey="notices" title="Notices">
								<fieldset disabled={!canEdit} className="border-0 p-0 m-0">
									<EventNotices
										initialData={events?.Notices || []}
										onValueChange={handleNotices}
									/>
								</fieldset>
							</Tab>
							<Tab eventKey="attendees" title="Attendees">
								<EventAttendees
									initialData={events || {}}
									onValueChange={handleAttendees}
									onFormValueChange={handleAttendeeForms}
									canEdit={canEdit}
								/>
							</Tab>
							<Tab eventKey="changelog" title="Change log">
								<EventChangelog
									eventData={events}
									// eslint-disable-next-line @typescript-eslint/no-explicit-any
									onValueChange={(changelog: any) => {
										// Optional: handle changelog data if needed
										console.log("Changelog updated:", changelog);
									}}
								/>
							</Tab>
						</Tabs>
					</Form>
					<Modal
						size="sm"
						className="text-center"
						show={showErrorModal}
						onHide={() => setShowErrorModal(false)}
						centered
					>
						<Modal.Body>
							{toastMessage} <br />
							<br />
							<Button
								variant="primary btn-sm"
								onClick={() => setShowErrorModal(false)}
							>
								Close
							</Button>
						</Modal.Body>
					</Modal>
					<Modal
						show={showDevInfo}
						onHide={() => setShowDevInfo(false)}
						centered
					>
						<Modal.Body>
							<p className="debug">
								DT id: {events._id}
								<br />
								{events.ForeignKeys?.Edumate?.EventId && (
									<span>
										Edumate event id: {events.ForeignKeys?.Edumate?.EventId}
									</span>
								)}
								{events.ForeignKeys?.Clipboard?.SessionId && (
									<span>
										Clipboard Session event id:{" "}
										{events.ForeignKeys?.Clipboard?.SessionId}
									</span>
								)}
							</p>
						</Modal.Body>
					</Modal>
				</Container>
			</>
		);
	} else {
		return <Loader />;
	}
};
